import { useState, useEffect, useCallback, useRef } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN", { minimumFractionDigits:0 });

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00" },
  { id:"amazon",   name:"Amazon",   color:"#c45000" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089" },
];

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"16px 18px" },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", fontSize:12, verticalAlign:"middle" },
};

// ── Mini bar chart ────────────────────────────────────────────
function BarChart({ data, color="#F47920", height=120, label="value" }) {
  if (!data || !data.length) return <div style={{ height, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", fontSize:12 }}>No data</div>;
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:4, height, padding:"0 4px" }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
          <div style={{ fontSize:9, color:"#9ca3af", textAlign:"center" }}>
            {d.value > 0 ? (label==="currency" ? "₹" + (d.value/1000).toFixed(0) + "k" : d.value) : ""}
          </div>
          <div style={{ width:"100%", borderRadius:"3px 3px 0 0", background:color, height: Math.max(2, (d.value/max)*(height-30)), transition:"height .3s" }}/>
          <div style={{ fontSize:9, color:"#9ca3af", textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", width:"100%", maxWidth:40 }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Mini line chart ───────────────────────────────────────────
function LineChart({ data, color="#F47920", height=100 }) {
  if (!data || data.length < 2) return <div style={{ height, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", fontSize:12 }}>Not enough data</div>;
  const max    = Math.max(...data.map((d) => d.value), 1);
  const min    = Math.min(...data.map((d) => d.value), 0);
  const range  = max - min || 1;
  const W      = 100 / (data.length - 1);
  const getY   = (v) => height - 20 - ((v - min) / range) * (height - 30);
  const points = data.map((d, i) => `${i * W},${getY(d.value)}`).join(" ");

  return (
    <div style={{ position:"relative", height }}>
      <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={"grad_" + color.replace("#","")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon points={`0,${height} ${points} 100,${height}`} fill={`url(#grad_${color.replace("#","")})`}/>
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        {data.map((d, i) => (
          <circle key={i} cx={i*W} cy={getY(d.value)} r="2" fill={color}/>
        ))}
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#9ca3af", marginTop:2 }}>
        <span>{data[0]?.label}</span>
        <span>{data[Math.floor(data.length/2)]?.label}</span>
        <span>{data[data.length-1]?.label}</span>
      </div>
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────
function ProgressBar({ value, max, color="#F47920", height=8 }) {
  const pct = max > 0 ? Math.min(100, (value/max)*100) : 0;
  return (
    <div style={{ height, background:"#f3f4f6", borderRadius:height/2, overflow:"hidden" }}>
      <div style={{ height:"100%", width:pct+"%", background:color, borderRadius:height/2, transition:"width .5s" }}/>
    </div>
  );
}

// ── compute analytics from orders + products ──────────────────
function computeAnalytics(orders, products, returns, dateFrom, dateTo) {
  const from = new Date(dateFrom);
  const to   = new Date(dateTo + "T23:59:59");

  const filteredOrders = orders.filter((o) => {
    const d = new Date(o.created_at || o.date || Date.now());
    return d >= from && d <= to;
  });

  // ── revenue trend (daily) ─────────────────────────────────
  const dayMap = {};
  filteredOrders.forEach((o) => {
    const day = new Date(o.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short" });
    dayMap[day] = (dayMap[day] || 0) + parseFloat(o.total_price || 0);
  });
  const revenueTrend = Object.entries(dayMap).slice(-14).map(([label, value]) => ({ label, value: Math.round(value) }));

  // ── orders trend ──────────────────────────────────────────
  const orderMap = {};
  filteredOrders.forEach((o) => {
    const day = new Date(o.created_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short" });
    orderMap[day] = (orderMap[day] || 0) + 1;
  });
  const ordersTrend = Object.entries(orderMap).slice(-14).map(([label, value]) => ({ label, value }));

  // ── platform breakdown ────────────────────────────────────
  const platformMap = {};
  filteredOrders.forEach((o) => {
    const pl = o.platform || o.source_name || "shopify";
    if (!platformMap[pl]) platformMap[pl] = { orders:0, revenue:0 };
    platformMap[pl].orders++;
    platformMap[pl].revenue += parseFloat(o.total_price || 0);
  });

  // ── top SKUs ──────────────────────────────────────────────
  const skuMap = {};
  filteredOrders.forEach((o) => {
    (o.line_items || []).forEach((item) => {
      const key = item.sku || item.variant_id || item.name;
      if (!skuMap[key]) skuMap[key] = { name:item.name, sku:key, qty:0, revenue:0, orders:0 };
      skuMap[key].qty     += item.quantity || 1;
      skuMap[key].revenue += parseFloat(item.price || 0) * (item.quantity || 1);
      skuMap[key].orders++;
    });
  });
  const topSkus = Object.values(skuMap).sort((a,b) => b.revenue - a.revenue).slice(0,10);

  // ── inventory health ──────────────────────────────────────
  const totalProducts   = products.length;
  const inStockCount    = products.filter((p) => Object.values(p.inventory||{}).some((v) => v > 10)).length;
  const lowStockCount   = products.filter((p) => Object.values(p.inventory||{}).some((v) => v > 0 && v <= 10)).length;
  const outOfStockCount = products.filter((p) => Object.values(p.inventory||{}).every((v) => !v)).length;
  const healthScore     = totalProducts > 0 ? Math.round((inStockCount / totalProducts) * 100) : 0;

  // ── return analytics ──────────────────────────────────────
  const totalOrders  = filteredOrders.length;
  const totalReturns = returns.filter((r) => {
    const d = new Date(r.createdAt || Date.now());
    return d >= from && d <= to;
  }).length;
  const returnRate   = totalOrders > 0 ? Math.round((totalReturns / totalOrders) * 100) : 0;

  // ── totals ────────────────────────────────────────────────
  const totalRevenue = filteredOrders.reduce((a,o) => a + parseFloat(o.total_price||0), 0);
  const avgOrderValue= totalOrders > 0 ? totalRevenue / totalOrders : 0;

  return {
    totalOrders, totalRevenue, avgOrderValue,
    revenueTrend, ordersTrend, platformMap, topSkus,
    inStockCount, lowStockCount, outOfStockCount, healthScore, totalProducts,
    totalReturns, returnRate,
  };
}

export default function PageReports({ toast, orders=[], products=[], returns=[] }) {
  const [tab,      setTab]      = useState("overview");
  const [dateFrom, setDateFrom] = useState(() => { const d=new Date(); d.setDate(1); return d.toISOString().split("T")[0]; });
  const [dateTo,   setDateTo]   = useState(new Date().toISOString().split("T")[0]);
  const [exporting,setExporting]= useState("");

  const analytics = computeAnalytics(orders, products, returns, dateFrom, dateTo);

  // ── Export Excel ──────────────────────────────────────────────
  const exportExcel = async (reportType) => {
    setExporting(reportType);
    try {
      const XLSX = await import("xlsx");
      const wb   = XLSX.utils.book_new();

      if (reportType === "revenue" || reportType === "all") {
        const rows = orders
          .filter((o) => { const d=new Date(o.created_at); return d>=new Date(dateFrom) && d<=new Date(dateTo+"T23:59:59"); })
          .map((o) => ({
            "Order ID":       o.name || o.id,
            "Date":           new Date(o.created_at).toLocaleDateString("en-IN"),
            "Customer":       o.customer ? o.customer.first_name+" "+o.customer.last_name : "Guest",
            "Platform":       o.source_name || "Shopify",
            "Items":          (o.line_items||[]).map((l) => l.name+"×"+l.quantity).join(", "),
            "Revenue (₹)":    parseFloat(o.total_price||0),
            "Payment":        o.financial_status || "—",
            "Fulfillment":    o.fulfillment_status || "unfulfilled",
          }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Revenue");
      }

      if (reportType === "inventory" || reportType === "all") {
        const rows = products.map((p) => ({
          "Product":        p.name,
          "SKU":            p.shopifySku,
          "Category":       p.category || "—",
          "Price (₹)":      p.price || 0,
          "Shopify Stock":  p.inventory?.shopify  || 0,
          "Amazon Stock":   p.inventory?.amazon   || 0,
          "Flipkart Stock": p.inventory?.flipkart || 0,
          "Myntra Stock":   p.inventory?.myntra   || 0,
          "Total Stock":    Object.values(p.inventory||{}).reduce((a,b)=>a+(b||0),0),
          "Status":         Object.values(p.inventory||{}).every((v)=>!v) ? "Out of Stock" : Object.values(p.inventory||{}).some((v)=>v>0&&v<=10) ? "Low Stock" : "In Stock",
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Inventory");
      }

      if (reportType === "sku" || reportType === "all") {
        const rows = analytics.topSkus.map((s, i) => ({
          "Rank":       i+1,
          "Product":    s.name,
          "SKU":        s.sku,
          "Units Sold": s.qty,
          "Revenue (₹)":Math.round(s.revenue),
          "Orders":     s.orders,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Top SKUs");
      }

      if (reportType === "returns" || reportType === "all") {
        const rows = returns.map((r) => ({
          "Return ID":   r._id,
          "Order ID":    r.orderId,
          "Product":     r.productName,
          "Platform":    r.platform,
          "Reason":      r.reason,
          "Status":      r.status,
          "Date":        new Date(r.createdAt||Date.now()).toLocaleDateString("en-IN"),
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Returns");
      }

      XLSX.writeFile(wb, `sellsync_report_${reportType}_${dateFrom}.xlsx`);
      toast("Report exported ✓", "success");
    } catch(e) { toast("Export failed: " + e.message, "error"); }
    finally { setExporting(""); }
  };

  // ── Export PDF (print) ────────────────────────────────────────
  const exportPDF = () => {
    const content = document.getElementById("report-content");
    if (!content) return;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>3June Report — ${dateFrom} to ${dateTo}</title>
      <style>
        * { box-sizing:border-box; margin:0; padding:0; font-family:Arial,sans-serif; }
        body { padding:24px; color:#111; font-size:12px; }
        h1 { font-size:20px; margin-bottom:4px; color:#F47920; }
        h2 { font-size:14px; margin:20px 0 8px; border-bottom:1px solid #e5e7eb; padding-bottom:4px; }
        .kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
        .kpi { border:1px solid #e5e7eb; border-radius:8px; padding:12px; }
        .kpi-label { font-size:10px; color:#6b7280; margin-bottom:4px; }
        .kpi-value { font-size:20px; font-weight:700; }
        table { width:100%; border-collapse:collapse; margin-bottom:16px; }
        th { background:#f9fafb; padding:6px 8px; text-align:left; font-size:10px; color:#6b7280; }
        td { padding:6px 8px; border-bottom:1px solid #f3f4f6; font-size:11px; }
        .orange { color:#F47920; }
        @media print { body { padding:0; } }
      </style></head><body>
      ${content.innerHTML}
      </body></html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 500);
    toast("PDF print dialog opened ✓", "success");
  };

  const { totalOrders, totalRevenue, avgOrderValue, revenueTrend, ordersTrend,
          platformMap, topSkus, inStockCount, lowStockCount, outOfStockCount,
          healthScore, totalProducts, totalReturns, returnRate } = analytics;

  return (
    <div>
      {/* date filter + export bar */}
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", marginBottom:14, display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontWeight:600, fontSize:13 }}>Period:</span>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <input type="date" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}/>
          <span style={{ color:"#9ca3af" }}>→</span>
          <input type="date" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
            value={dateTo} onChange={(e) => setDateTo(e.target.value)}/>
        </div>
        <span style={{ fontSize:12, color:"#9ca3af" }}>{totalOrders} orders in range</span>
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          <button style={{ ...S.btn("#15803d"), opacity:exporting==="all"?0.7:1 }}
            onClick={() => exportExcel("all")} disabled={!!exporting}>
            {exporting==="all" ? "Exporting…" : "⬇ Excel"}
          </button>
          <button style={S.btn("#2563eb")} onClick={exportPDF}>🖨️ PDF</button>
        </div>
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, flexWrap:"wrap" }}>
        {[
          { id:"overview",   label:"📊 Overview"          },
          { id:"revenue",    label:"💰 Revenue"            },
          { id:"inventory",  label:"📦 Inventory Health"   },
          { id:"sku",        label:"🏆 Top SKUs"           },
          { id:"platforms",  label:"🌐 Platform Analysis"  },
          { id:"returns",    label:"↩️ Returns"            },
        ].map((t) => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* printable content wrapper */}
      <div id="report-content">

        {/* ══ OVERVIEW ══ */}
        {tab==="overview" && (
          <>
            {/* KPIs */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
              {[
                { label:"Total Revenue",      value:INR(totalRevenue),         sub:"Period total",             color:"#F47920", trend:revenueTrend },
                { label:"Total Orders",       value:totalOrders,               sub:"Avg "+INR(avgOrderValue)+" / order", color:"#2563eb", trend:ordersTrend },
                { label:"Inventory Health",   value:healthScore+"%",           sub:inStockCount+" products in stock", color:healthScore>=70?"#15803d":healthScore>=40?"#d97706":"#dc2626" },
                { label:"Return Rate",        value:returnRate+"%",            sub:totalReturns+" returns",    color:returnRate<=5?"#15803d":returnRate<=15?"#d97706":"#dc2626" },
              ].map((k) => (
                <div key={k.label} style={S.kpi}>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:4 }}>{k.label}</div>
                  <div style={{ fontSize:24, fontWeight:700, color:k.color, marginBottom:4 }}>{k.value}</div>
                  <div style={{ fontSize:11, color:"#9ca3af", marginBottom:8 }}>{k.sub}</div>
                  {k.trend && <LineChart data={k.trend} color={k.color} height={50}/>}
                </div>
              ))}
            </div>

            {/* Revenue + Orders trend side by side */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
              <div style={S.card}>
                <div style={S.cardHdr}>
                  <span>Revenue Trend</span>
                  <button style={{ ...S.btnOut, fontSize:10 }} onClick={() => exportExcel("revenue")}>⬇ Export</button>
                </div>
                <div style={{ padding:16 }}>
                  <BarChart data={revenueTrend} color="#F47920" height={140} label="currency"/>
                </div>
              </div>
              <div style={S.card}>
                <div style={S.cardHdr}>
                  <span>Orders Trend</span>
                  <button style={{ ...S.btnOut, fontSize:10 }} onClick={() => exportExcel("revenue")}>⬇ Export</button>
                </div>
                <div style={{ padding:16 }}>
                  <BarChart data={ordersTrend} color="#2563eb" height={140}/>
                </div>
              </div>
            </div>

            {/* inventory health + platform split */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div style={S.card}>
                <div style={S.cardHdr}><span>Inventory Health</span></div>
                <div style={{ padding:16 }}>
                  {[
                    { label:"In Stock",    value:inStockCount,    total:totalProducts, color:"#22c55e" },
                    { label:"Low Stock",   value:lowStockCount,   total:totalProducts, color:"#f59e0b" },
                    { label:"Out of Stock",value:outOfStockCount, total:totalProducts, color:"#ef4444" },
                  ].map((item) => (
                    <div key={item.label} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:6 }}>
                        <span style={{ fontWeight:500 }}>{item.label}</span>
                        <span style={{ fontWeight:700, color:item.color }}>{item.value} <span style={{ color:"#9ca3af", fontWeight:400 }}>/ {item.total}</span></span>
                      </div>
                      <ProgressBar value={item.value} max={item.total} color={item.color}/>
                    </div>
                  ))}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", marginTop:8 }}>
                    <div style={{ fontSize:36, fontWeight:800, color:healthScore>=70?"#22c55e":healthScore>=40?"#f59e0b":"#ef4444" }}>{healthScore}%</div>
                    <div style={{ marginLeft:12, fontSize:13, color:"#6b7280" }}>Health Score</div>
                  </div>
                </div>
              </div>

              <div style={S.card}>
                <div style={S.cardHdr}><span>Platform Revenue Split</span></div>
                <div style={{ padding:16 }}>
                  {Object.entries(platformMap).length === 0 ? (
                    <div style={{ textAlign:"center", color:"#9ca3af", padding:20 }}>No order data for this period</div>
                  ) : (
                    Object.entries(platformMap).map(([pl, data]) => {
                      const plInfo = PLATFORMS.find((p) => p.id===pl) || { name:pl, color:"#9ca3af" };
                      const maxRev = Math.max(...Object.values(platformMap).map((d) => d.revenue), 1);
                      return (
                        <div key={pl} style={{ marginBottom:14 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:6 }}>
                            <span style={{ fontWeight:600, color:plInfo.color }}>{plInfo.name}</span>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontWeight:700 }}>{INR(data.revenue)}</div>
                              <div style={{ fontSize:10, color:"#9ca3af" }}>{data.orders} orders</div>
                            </div>
                          </div>
                          <ProgressBar value={data.revenue} max={maxRev} color={plInfo.color}/>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══ REVENUE ══ */}
        {tab==="revenue" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:14 }}>
              {[
                { label:"Total Revenue",   value:INR(totalRevenue),          color:"#F47920" },
                { label:"Total Orders",    value:totalOrders,                color:"#2563eb" },
                { label:"Avg Order Value", value:INR(Math.round(avgOrderValue)), color:"#15803d" },
              ].map((k) => (
                <div key={k.label} style={S.kpi}>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
                  <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Revenue Trend (Daily)</span>
                <button style={{ ...S.btn("#15803d"), fontSize:11 }} onClick={() => exportExcel("revenue")}>⬇ Excel</button>
              </div>
              <div style={{ padding:16 }}>
                <BarChart data={revenueTrend} color="#F47920" height={180} label="currency"/>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Order Details</span>
                <span style={{ fontSize:12, color:"#9ca3af" }}>{totalOrders} orders</span>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Order","Date","Customer","Platform","Items","Revenue","Payment","Fulfillment"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {orders.filter((o) => { const d=new Date(o.created_at); return d>=new Date(dateFrom)&&d<=new Date(dateTo+"T23:59:59"); })
                      .slice(0,50).map((o,i) => (
                      <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{o.name}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                        <td style={{ ...S.td, fontWeight:500 }}>{o.customer ? o.customer.first_name+" "+o.customer.last_name : "Guest"}</td>
                        <td style={S.td}><span style={{ fontSize:10, background:"#f3f4f6", padding:"1px 6px", borderRadius:4, fontWeight:600 }}>{o.source_name||"shopify"}</span></td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{(o.line_items||[]).slice(0,2).map((l)=>l.name+"×"+l.quantity).join(", ")}</td>
                        <td style={{ ...S.td, fontWeight:700, color:"#F47920" }}>{INR(o.total_price)}</td>
                        <td style={S.td}><span style={{ fontSize:10, background:o.financial_status==="paid"?"#f0fdf4":"#fffbeb", color:o.financial_status==="paid"?"#15803d":"#d97706", padding:"2px 6px", borderRadius:10, fontWeight:600 }}>{o.financial_status||"pending"}</span></td>
                        <td style={S.td}><span style={{ fontSize:10, background:o.fulfillment_status==="fulfilled"?"#f0fdf4":"#f3f4f6", color:o.fulfillment_status==="fulfilled"?"#15803d":"#6b7280", padding:"2px 6px", borderRadius:10, fontWeight:600 }}>{o.fulfillment_status||"unfulfilled"}</span></td>
                      </tr>
                    ))}
                    {totalOrders===0 && <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No orders in selected period</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ══ INVENTORY HEALTH ══ */}
        {tab==="inventory" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
              {[
                { label:"Total Products",  value:totalProducts,   color:"#2563eb" },
                { label:"In Stock",        value:inStockCount,    color:"#15803d" },
                { label:"Low Stock",       value:lowStockCount,   color:"#d97706" },
                { label:"Out of Stock",    value:outOfStockCount, color:"#dc2626" },
              ].map((k) => (
                <div key={k.label} style={S.kpi}>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
                  <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Inventory by Product</span>
                <button style={{ ...S.btn("#15803d"), fontSize:11 }} onClick={() => exportExcel("inventory")}>⬇ Excel</button>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Product","SKU","Shopify","Amazon","Flipkart","Myntra","Total","Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {[...products].sort((a,b) => {
                      const ta = Object.values(a.inventory||{}).reduce((s,v)=>s+(v||0),0);
                      const tb = Object.values(b.inventory||{}).reduce((s,v)=>s+(v||0),0);
                      return ta - tb; // lowest stock first
                    }).map((p,i) => {
                      const total = Object.values(p.inventory||{}).reduce((s,v)=>s+(v||0),0);
                      const status= total===0?"Out of Stock":Object.values(p.inventory||{}).some((v)=>v>0&&v<=10)?"Low Stock":"In Stock";
                      const stColor= status==="Out of Stock"?"#dc2626":status==="Low Stock"?"#d97706":"#15803d";
                      const stBg   = status==="Out of Stock"?"#fef2f2":status==="Low Stock"?"#fffbeb":"#f0fdf4";
                      return (
                        <tr key={i} style={{ background:status==="Out of Stock"?"#fff5f5":status==="Low Stock"?"#fffdf0":"" }}
                          onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"}
                          onMouseLeave={(e)=>e.currentTarget.style.background=status==="Out of Stock"?"#fff5f5":status==="Low Stock"?"#fffdf0":""}>
                          <td style={{ ...S.td, fontWeight:500, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</td>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#6b7280" }}>{p.shopifySku}</td>
                          {["shopify","amazon","flipkart","myntra"].map((pl) => (
                            <td key={pl} style={{ ...S.td, fontWeight:600, textAlign:"center", color:(p.inventory?.[pl]||0)===0?"#dc2626":(p.inventory?.[pl]||0)<=10?"#d97706":"#111" }}>
                              {p.inventory?.[pl]||0}
                            </td>
                          ))}
                          <td style={{ ...S.td, fontWeight:700, color:total===0?"#dc2626":"#111", textAlign:"center" }}>{total}</td>
                          <td style={S.td}><span style={{ background:stBg, color:stColor, padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:600 }}>{status}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ══ TOP SKUs ══ */}
        {tab==="sku" && (
          <>
            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Top Performing SKUs</span>
                <button style={{ ...S.btn("#15803d"), fontSize:11 }} onClick={() => exportExcel("sku")}>⬇ Excel</button>
              </div>
              <div style={{ padding:16 }}>
                <BarChart
                  data={topSkus.slice(0,8).map((s) => ({ label:s.name.split(" ").slice(0,2).join(" "), value:Math.round(s.revenue) }))}
                  color="#F47920" height={180} label="currency"/>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}><span>SKU Performance Table</span></div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Rank","Product","SKU","Units Sold","Revenue","Orders","Avg Price"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {topSkus.map((s,i) => {
                      const maxRev = topSkus[0]?.revenue || 1;
                      return (
                        <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                          <td style={{ ...S.td, fontWeight:700, color:i===0?"#F47920":i===1?"#6b7280":i===2?"#c45000":"#374151" }}>
                            {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                          </td>
                          <td style={{ ...S.td, fontWeight:500, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</td>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#6b7280" }}>{s.sku}</td>
                          <td style={{ ...S.td, fontWeight:600, textAlign:"center" }}>{s.qty}</td>
                          <td style={{ ...S.td, fontWeight:700, color:"#F47920" }}>
                            {INR(Math.round(s.revenue))}
                            <div style={{ marginTop:4 }}><ProgressBar value={s.revenue} max={maxRev} color="#F47920" height={4}/></div>
                          </td>
                          <td style={{ ...S.td, textAlign:"center" }}>{s.orders}</td>
                          <td style={S.td}>{s.qty > 0 ? INR(Math.round(s.revenue/s.qty)) : "—"}</td>
                        </tr>
                      );
                    })}
                    {topSkus.length===0 && <tr><td colSpan={7} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No order data for this period</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ══ PLATFORM ANALYSIS ══ */}
        {tab==="platforms" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12, marginBottom:14 }}>
              {PLATFORMS.map((pl) => {
                const data  = platformMap[pl.id] || { orders:0, revenue:0 };
                const stock = products.reduce((a,p)=>a+(p.inventory?.[pl.id]||0),0);
                return (
                  <div key={pl.id} style={{ ...S.kpi, borderTop:`3px solid ${pl.color}` }}>
                    <div style={{ fontWeight:700, color:pl.color, marginBottom:10 }}>{pl.name}</div>
                    {[
                      ["Orders",  data.orders],
                      ["Revenue", INR(Math.round(data.revenue))],
                      ["Stock",   stock + " units"],
                    ].map(([l,v]) => (
                      <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
                        <span style={{ color:"#6b7280" }}>{l}</span>
                        <span style={{ fontWeight:600 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}><span>Platform Revenue Comparison</span></div>
              <div style={{ padding:16 }}>
                <BarChart
                  data={PLATFORMS.filter((pl)=>platformMap[pl.id]).map((pl) => ({
                    label: pl.name,
                    value: Math.round(platformMap[pl.id]?.revenue || 0),
                  }))}
                  color="#F47920" height={180} label="currency"/>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}><span>Stock Distribution by Platform</span></div>
              <div style={{ padding:16 }}>
                <BarChart
                  data={PLATFORMS.map((pl) => ({
                    label: pl.name,
                    value: products.reduce((a,p)=>a+(p.inventory?.[pl.id]||0),0),
                  }))}
                  color="#2563eb" height={140}/>
              </div>
            </div>
          </>
        )}

        {/* ══ RETURNS ══ */}
        {tab==="returns" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:14 }}>
              {[
                { label:"Total Returns",   value:totalReturns,    color:"#dc2626" },
                { label:"Return Rate",     value:returnRate+"%",  color:returnRate<=5?"#15803d":returnRate<=15?"#d97706":"#dc2626" },
                { label:"vs Total Orders", value:totalOrders+" orders", color:"#2563eb" },
              ].map((k) => (
                <div key={k.label} style={S.kpi}>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
                  <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div style={S.card}>
              <div style={S.cardHdr}>
                <span>Returns Detail</span>
                <button style={{ ...S.btn("#15803d"), fontSize:11 }} onClick={() => exportExcel("returns")}>⬇ Excel</button>
              </div>
              {returns.length===0 ? (
                <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>No returns in this period</div>
              ) : (
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      {["Return ID","Order","Product","Platform","Reason","Status","Date"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {returns.slice(0,50).map((r,i) => (
                        <tr key={i} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{String(r._id||"").slice(-8)}</td>
                          <td style={{ ...S.td, fontSize:11 }}>{r.orderId}</td>
                          <td style={{ ...S.td, fontWeight:500 }}>{r.productName}</td>
                          <td style={S.td}>{r.platform}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{r.reason}</td>
                          <td style={S.td}><span style={{ fontSize:11, background:"#f3f4f6", padding:"2px 8px", borderRadius:10, fontWeight:600 }}>{r.status}</span></td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(r.createdAt||Date.now()).toLocaleDateString("en-IN")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
